import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useTranslation } from 'react-i18next';

// Theme for button colors
const theme = {
  white: {
    default: '#DFBD69',  
    hover: '#b7954d', 
  },
  grey: {
    default: '#DFBD69', 
    hover: '#b7954d',
  },
};

// Button styling
const Button = styled.button`
  background-color: ${(props) => {
    if (props.disabled) return '#333';
    if (props.selected) return '#DFBD69';
    return '#0A031E';
  }};

  color: ${(props) => {
    if (props.disabled) return '#666';
    if (props.selected) return '#000';
    return '#DFBD69';
  }};

  border: 1px solid rgba(223,189,105,0.18);
  padding: 10px 18px;
  border-radius: 10px;
  outline: 0;
  // border: 1px solid
  //   ${(props) => (props.theme === 'white' ? '#000000' : '#d0d0d0')};
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  transition: background-color 250ms ease;
  margin: 10px;
  &:hover {
    background-color: ${(props) =>
      props.disabled ? '#333' : props.selected ? '#DFBD69' : '#111111'};
    color: ${(props) =>
      props.disabled ? '#666' : props.selected ? '#000' : '#DFBD69'};
  }
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
  ${(props) =>
    props.selected &&
    `
    box-shadow: 0 0 0 2px #b7954d;
    font-weight: bold;
    transform: scale(1.08);
  `}

  border-radius: ${(props) => props.radius || '10px'};
  width: ${(props) => props.width || 'auto'};
`;

Button.defaultProps = {
  theme: 'grey',
  radius: '10px',
  width: 'auto',
};

// Container styling
const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 10px;
  margin: 0 15%;
  padding: 20px;
  box-sizing: border-box;
  min-height: 100vh;
  background-color: transparent; 
  color: #fff2d2;
  font-family: 'DMSans', Arial, sans-serif;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
  }
`;

// Panel styling
const Panel = styled.div`
  padding: 20px;
  border-radius: 10px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
  
  &.leftpanel {
    background-color:transparent;
    border-right: 1px solid rgba(201,168,75,0.12);

    @media (max-width: 768px) {
      border-right: none;
    }
  }

  &.rightpanel {
    background-color: transparent;

    @media (max-width: 768px) {
      border-right: none;
      border-radius: 0;
    }
  }
`;

// Image styling for Master profile
const PanelImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 2px solid rgba(223,189,105,0.3);
  margin-bottom: 10px;
`;

// Calendar dropdown styling
const CalendarDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  background-color: #ffffff;
  border: 1px solid #d0d0d0;
  border-radius: 5px;
  padding: 10px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  width: max-content;

  @media (max-width: 768px) {
    width: 100%;
    left: 0;
    top: 100%;
  }
`;

// Time selection container styling
const TimeSelectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
`;

const ModalContent = styled.div`
  background-color: rgba(10,3,30,0.95);
  padding: 32px 28px 24px 28px;
  border-radius: 15px;
  width: 400px;
  max-width: 90vw;
  color: #fff2d2;
  position: relative;
  box-shadow: 0px 8px 36px rgba(0, 0, 0, 0.4);
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #DFBD69;
  font-size: 1.8rem;
  font-weight: bold;
  cursor: pointer;
  z-index: 2;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ModalTitle = styled.h2`
  color: #DFBD69;
  margin-bottom: 18px;
`;

const ModalMessage = styled.div`
  margin-bottom: 24px;
  color: #ccc;
  font-size: 1rem;
`;

const ModalButton = styled.button`
  background: #DFBD69;
  color: #111;
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #b7954d;
  }
`;

function Schedule() {
  const [value, setValue] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const navigate = useNavigate();
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('language') || 'en'
  );

  const handleDateChange = (newDate) => {
    setValue(newDate);
    setShowCalendar(false);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  // Toggle selection of time slot
  const handleSlotClick = (index) => {
    if (selectedSlots.includes(index)) {
      setSelectedSlots(selectedSlots.filter((i) => i !== index)); // Deselect if already selected
    } else if (selectedSlots.length < 4) {
      setSelectedSlots([...selectedSlots, index]); // Select up to 4 slots
    }
  };

  // const checkLoginAndOrder = () => {
  //   const isLoggedIn = localStorage.getItem('isLoggedIn');
  //   if (isLoggedIn === 'true') {
  //     navigate('/schedule2', {
  //       state: { selectedMaster }
  //     });
  //   } else {
  //     navigate('/login', { state: { message: 'You need to log in before placing an order.' } });
  //   }
  // };

  const fetchApiData = async () => {
    try {
      const response = await fetch('https://xianhefairy.com/LotusFairy_API/api/Booking/getBL?STATUS=2');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      localStorage.setItem('blockingData', data);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.log(error);
    }
  };

  // Fetch masters from API and set into state
  useEffect(() => {
    let mounted = true;
    const loadMasters = async () => {
      setLoading(true);
      try {
        // Get user ID and language ID from localStorage
        const userId = localStorage.getItem('userId') || '';
        const langId = localStorage.getItem('languageId') || '2224'; // Default to English (2224)

        // Build API URL with query parameters
        const apiUrl = `https://xianhefairy.com/LotusFairy_API/api/profile/GetAll?CustId=${userId}&LangId=${langId}`;
        
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error(`Failed to fetch masters: ${res.status}`);
        const api = await res.json();

        // Map API to local master shape and provide fallback images
        const baseUrl = 'https://xianhefairy.com';

        const mapped = api
          .filter((p) => p.DisplayName && p.Status === 0) // basic filter
          .map((p) => {
            // Normalize Photo: ignore null/empty/'null' values, handle relative paths
            let photo = null;
            if (p.Photo && p.Photo !== 'null') {
              const trimmed = String(p.Photo).trim();
              if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                photo = trimmed;
              } else if (trimmed.startsWith('/')) {
                photo = `${baseUrl}${trimmed}`;
              } else {
                // assume relative path
                photo = `${baseUrl}/${trimmed}`;
              }
            }

            return {
              id: p.ID,
              name: p.DisplayName,
              image: photo,
              description: p.Address || '',
              // Use API Rate directly; treat 0 as a valid rate. If missing, default to 0.
              rate: p.Rate !== null && p.Rate !== undefined ? Number(p.Rate) : 0,
              gem_rate: p.Gems !== null && p.Gems !== undefined ? Number(p.Gems) : 0,
              raw: p,
            };
          });

        if (mounted) {
          // Sort so DisplayName containing 'Master' appear first
          const sorted = mapped.sort((a, b) => {
            const aIsMaster = /\bMaster\b/i.test(a.name) ? 0 : 1;
            const bIsMaster = /\bMaster\b/i.test(b.name) ? 0 : 1;
            if (aIsMaster !== bIsMaster) return aIsMaster - bIsMaster;
            // keep original order otherwise
            return 0;
          });

          // Ensure 'Master SiewLi' is first if present
          const siewliIndex = sorted.findIndex((m) => /siewli/i.test(m.name));
          if (siewliIndex > 0) {
            const [siewli] = sorted.splice(siewliIndex, 1);
            sorted.unshift(siewli);
          }

          setMasters(sorted);
          // default selected master to Master SiewLi if found, otherwise first
          const defaultMaster = sorted.find((m) => /siewli/i.test(m.name)) || sorted[0];
          if (defaultMaster) setSelectedMaster(defaultMaster);
          setError(null);
        }
      } catch (err) {
        console.error(err);
        if (mounted) setError(err.message || 'Failed to load masters');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadMasters();
    return () => {
      mounted = false;
    };
  }, [selectedLanguage]); // Add selectedLanguage as dependency to refetch when language changes

  const checkLoginAndOrder = () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (isLoggedIn === 'true') {
      console.log( selectedMaster )
      navigate('/schedule2', {
        state: { selectedMaster }
      });
    } else {
      setShowLoginModal(true);
    }
  };
  const handleLoginRedirect = () => {
    setShowLoginModal(false);
    // navigate('/login');
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };
  

  const toggleCalendarDropdown = () => {
    setShowCalendar(!showCalendar);
  };

  return (
    <Container>
      <Panel className="leftpanel">
        {selectedMaster && <PanelImage src={selectedMaster.image} alt="Master" />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '5px' }}>
          {loading && <div style={{ color: '#DFBD69', padding: '10px' }}>Loading masters...</div>}
          {error && <div style={{ color: 'white', padding: '10px' }}>Error: {error}</div>}
          {masters.map((master) => (
            <Button key={master.id} onClick={() => setSelectedMaster(master)} selected={selectedMaster?.id === master.id}>
              {master.name}
            </Button>
          ))}
        </div>
      </Panel>
      <Panel className="rightpanel">
        <h1 style={{color:"#DFBD69"}}>{t('appointmentsPage.chooseMaster')}</h1>
        <h2 style={{color:"white"}}>{selectedMaster?.name}</h2>
        <div style={{ position: 'relative', width: '100%' }}>
        <p style={{color:"white"}}>{selectedMaster?.description}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', color: '#DFBD69' }}>
          <Button onClick={checkLoginAndOrder} theme="white" radius="28px" width="220px">
            {t('appointmentsPage.confirmMaster')}
          </Button>
        </div>

      </Panel>
      {showLoginModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal} aria-label="Close">
              ×
            </CloseButton>
            <ModalTitle>
              {t('appointmentsPage.loginRequired') || 'Login Required'}
            </ModalTitle>
            <ModalMessage>
              {t('appointmentsPage.loginMessage') || 'You need to log in before creating a schedule.'}
            </ModalMessage>
            <ModalButton onClick={handleCloseModal}>
              {t('appointmentsPage.close') || 'Close'}
            </ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default Schedule;
